import { composeHeading, createRouteMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import { SumSubMarketNameDataTransformer, UndefinedStateToSumGermanyDashboardDataTransformer } from '../../../../data_transformers/dashboard_data_transformer';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { createIdNameTransformers } from '../../../../dashboard-config/granularities/granularity-profile';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import {
  KpiColumnDataTransformer,
  KpiExportDataTransformer,
  KpiLabelDataTransformer,
  KpiValueDisplayDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import { createTimeFilters, createPeriodLabelTransformers, createPeriodYearTransformers, resolveTimeFilter } from '../../../_shared/time-filters';
import { ComparisonMetricProfile } from '../comparison-metric.profile';
import { DashboardComparisonFourGridDefinition } from './dashboard-comparison-four-grid.definition';
import {
  DashboardComparisonFourGridShell,
  dashboardComparisonFourGridShellProfiles,
} from './dashboard-comparison-four-grid-shell.profile';

const VALUE_KEY = 'value';

export type CreateDashboardComparisonFourGridDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardComparisonFourGridShell,
    ComparisonMetricProfile
  >;

export function createDashboardComparisonFourGridDefinition(
  input: CreateDashboardComparisonFourGridDefinitionInput,
): DashboardComparisonFourGridDefinition {
  const { axes, shell, metric } = resolveProduct(input, dashboardComparisonFourGridShellProfiles);
  const granularityId = axes.granularity.id;
  const valueKey = VALUE_KEY;
  const time = resolveTimeFilter(axes, 'range');
  // Rows are the granularity's groups; each panel picks one value of its primary scope.
  const { group, scope: [panelScope] } = axes.granularity;

  return defineDashboard<DashboardComparisonFourGridDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading(
        [shell.headingCompareLabel, metric.headingPrefix, metric.headingMetricValue(filterValues), axes.granularity.byPhrase],
        metric.headingTimeValue(filterValues),
      ),

    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps),
      ...createKpiFilters(axes.metric, granularityId),
      createRouteMethodologyFilter(deps),
    ],

    preFilterTransformers: (deps) => [
      ...createPeriodLabelTransformers(deps, axes),
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
    ],

    postFilterTransformers: (deps) => [
      new SumSubMarketNameDataTransformer(deps.localDatasetsService),
      ...createIdNameTransformers(deps, granularityId),
      new KpiLabelDataTransformer(),
      ...createPeriodYearTransformers(axes),
    ],

    chartTransformers: (_deps) => [
      new KpiColumnDataTransformer({ measure: 'value', resultColumn: valueKey, context: axes.kpiContext }),
      new KpiValueDisplayDataTransformer({
        valueColumnKey: valueKey,
        resultColumnKey: valueKey + '_display',
        countUnit: axes.metric.labels.displayUnit,
      }),
    ],

    exportTransformers: (_deps) => [new KpiExportDataTransformer({ kpis: axes.metric.kpis(), percentageMeasures: [] })],

    view: {
      valueKey,
      // Absent rather than false for count metrics, as the profiles declared it.
      popup: { ...metric.popup, ...(isKEur(axes) ? { kEur: true } : {}) },
      uiSettings: {
        rowKey: group.nameKey,
        colKey: metric.colKey,
        metric: panelScope.filterName,
        filterKey: panelScope.valueKey,
      },
    },
  });
}
